/**
 * MediaGrid — responsive grid of uploaded media files.
 *
 * Displays image thumbnails for raster images and a file icon for PDFs/SVG.
 * Each card shows filename, file size, and a delete button with confirmation.
 */

import type { MediaItem } from '@/hooks/useCms'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const IMAGE_MIME_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp'])

function isImage(mimeType: string): boolean {
  return IMAGE_MIME_TYPES.has(mimeType)
}

// ─── File icon for non-image types ────────────────────────────────────────────

function FileIcon({ mimeType }: { mimeType: string }) {
  const label = mimeType === 'application/pdf' ? 'PDF' : mimeType === 'image/svg+xml' ? 'SVG' : 'FILE'
  return (
    <div className="flex h-full w-full items-center justify-center bg-gray-100">
      <span className="rounded bg-gray-300 px-2 py-1 text-xs font-bold uppercase text-gray-600">
        {label}
      </span>
    </div>
  )
}

// ─── MediaCard ────────────────────────────────────────────────────────────────

function MediaCard({
  item,
  onDelete,
}: {
  item: MediaItem
  onDelete: (id: string) => void
}) {
  function handleDelete() {
    if (window.confirm(`Eliminar "${item.filename}"? Esta accion no se puede deshacer.`)) {
      onDelete(item.id)
    }
  }

  return (
    <div className="group relative overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md">
      {/* Thumbnail area */}
      <div className="relative h-36 w-full overflow-hidden">
        {isImage(item.mime_type) ? (
          <img
            src={item.url}
            alt={item.alt ?? item.filename}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <FileIcon mimeType={item.mime_type} />
        )}

        {/* Delete button — shown on hover */}
        <button
          onClick={handleDelete}
          className="absolute right-2 top-2 hidden rounded bg-red-600 px-2 py-1 text-xs text-white opacity-90 hover:bg-red-700 group-hover:block"
          title="Eliminar archivo"
          type="button"
        >
          Eliminar
        </button>
      </div>

      {/* File info */}
      <div className="p-2">
        <p
          className="truncate text-xs font-medium text-gray-800"
          title={item.filename}
        >
          {item.filename}
        </p>
        <p className="mt-0.5 text-xs text-gray-500">{formatFileSize(item.file_size)}</p>
      </div>
    </div>
  )
}

// ─── MediaGrid ────────────────────────────────────────────────────────────────

interface MediaGridProps {
  items: MediaItem[]
  onDelete: (id: string) => void
}

export function MediaGrid({ items, onDelete }: MediaGridProps) {
  if (items.length === 0) {
    return (
      <div className="flex items-center justify-center rounded-lg border border-dashed border-gray-300 py-16 text-sm text-gray-500">
        No hay archivos. Sube el primero arriba.
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
      {items.map((item) => (
        <MediaCard key={item.id} item={item} onDelete={onDelete} />
      ))}
    </div>
  )
}
